import { useQuery, useQueryClient, QueryClient } from '@tanstack/react-query';
import { getCurrentTenantId } from '@/lib/auth-utils';

// Query keys for tenant-related queries
export const tenantKeys = {
  all: ['tenant'] as const,
  current: () => [...tenantKeys.all, 'current'] as const,
};

// Cache configuration
const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const GC_TIME = 10 * 60 * 1000; // 10 minutes

/**
 * Hook to get the current tenant ID with caching
 * This is cached aggressively since tenant ID rarely changes
 */
export function useCurrentTenantId() {
  return useQuery({
    queryKey: tenantKeys.current(),
    queryFn: getCurrentTenantId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * Utility function to invalidate tenant queries
 * Call this when user switches tenants or projects change
 */
export function invalidateTenantQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: tenantKeys.all,
  });
}

/**
 * Utility function to get cached tenant ID synchronously
 * Returns null if not cached
 */
export function getCachedTenantId(queryClient: QueryClient): string | null {
  return queryClient.getQueryData(tenantKeys.current()) || null;
}
